import { useState, useMemo } from 'react';
import { Search, RefreshCw, X } from 'lucide-react';
import { useAdminPayments, useAdminPayment, useRecoverAdminBillingPayment } from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { ConfirmModal, SectionHeader, Spinner } from '../../components/ui';
import { cn, fmtDateTime } from '../../lib/utils';

const PAGE_SIZE = 50;
const PAYMENT_SOURCES  = ['BILLING', 'CLOUD_VPS'];
const PAYMENT_STATUSES = ['CREATED', 'PAID', 'FAILED'];

const STATUS_STYLE = {
  PAID:    'text-bull bg-bull/10 border-bull/30',
  CREATED: 'text-warn bg-warn/10 border-warn/30',
  FAILED:  'text-bear bg-bear/10 border-bear/30',
};

const SOURCE_LABEL = {
  BILLING:   'Billing',
  CLOUD_VPS: 'Cloud VPS',
};

function fmtAmount(item) {
  if (item.amount_rupees != null) return '₹' + Number(item.amount_rupees).toFixed(2);
  if (item.amount_paise  != null) return '₹' + (Number(item.amount_paise) / 100).toFixed(2);
  return '—';
}

function StatusBadge({ status }) {
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded border text-2xs font-mono',
      STATUS_STYLE[status] ?? 'text-txt-muted border-bg-border',
    )}>
      {status ?? '—'}
    </span>
  );
}

function SourceBadge({ source }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded border text-2xs font-mono text-info bg-info/10 border-info/30">
      {SOURCE_LABEL[source] ?? source ?? '—'}
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest">{label}</span>
      <div className="text-xs text-txt-primary break-words">{value ?? <span className="text-txt-muted">—</span>}</div>
    </div>
  );
}

// Exactly what the backend recovery endpoint can return, in Super Admin wording.
// It re-reads Razorpay evidence only; it can never create or force a payment.
const RECOVERY_OUTCOME_COPY = {
  paid: {
    tone: 'bull',
    text: 'Razorpay evidence confirmed a captured payment. The order is marked paid and its original purchased benefit was applied.',
  },
  pending: {
    tone: 'warn',
    text: 'No captured payment is currently available from Razorpay. Nothing was changed and the check can be repeated later.',
  },
  support_required: {
    tone: 'bear',
    text: 'This order cannot be resolved automatically and needs manual review. Nothing was changed.',
  },
};
const RECOVERY_FAILED_TEXT =
  'The recovery check could not be completed. Nothing was changed for this order.';

const RECOVERY_CONFIRM_MESSAGE =
  'This checks authoritative Razorpay evidence for this order and applies the original purchased benefit only if Razorpay confirms a captured payment. '
  + 'It does not create a payment, does not charge the customer, does not unblock an account, and cannot activate a subscription without a captured payment.';

function RecoveryPanel({ payment }) {
  const isSuperAdmin = useSessionStore((s) => s.isSuperAdmin);
  const { mutateAsync: recoverPayment, isPending } = useRecoverAdminBillingPayment();
  const [confirming, setConfirming] = useState(false);
  const [outcome, setOutcome] = useState(null);

  // Super Admin only, billing orders only, and only while the order is still
  // unresolved. A paid order with a recorded payment id needs no recovery.
  const eligible = isSuperAdmin
    && payment?.source === 'BILLING'
    && payment?.status !== 'PAID'
    && payment?.source_id != null;
  if (!eligible) return null;

  async function runRecovery() {
    if (isPending) return;
    setOutcome(null);
    try {
      const result = await recoverPayment(payment.source_id);
      setOutcome({ value: result?.outcome ?? 'pending' });
    } catch {
      setOutcome({ failed: true });
    }
  }

  const copy = outcome && !outcome.failed ? RECOVERY_OUTCOME_COPY[outcome.value] : null;

  return (
    <div className="border border-bg-border rounded p-3 space-y-2">
      <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Payment Recovery</p>
      <p className="text-2xs text-txt-muted">{RECOVERY_CONFIRM_MESSAGE}</p>
      <button
        onClick={() => setConfirming(true)}
        disabled={isPending}
        className="btn-ghost text-2xs py-1 px-2 border border-bg-border disabled:opacity-50"
      >
        {isPending ? 'Checking…' : 'Verify with Razorpay'}
      </button>
      {outcome && (
        <p className={cn(
          'text-2xs rounded border px-2 py-1.5',
          copy?.tone === 'bull' && 'text-bull bg-bull/5 border-bull/30',
          copy?.tone === 'warn' && 'text-warn bg-warn/5 border-warn/30',
          (!copy || copy.tone === 'bear') && 'text-bear bg-bear/5 border-bear/30',
        )}>
          {copy ? copy.text : RECOVERY_FAILED_TEXT}
        </p>
      )}
      <ConfirmModal
        open={confirming}
        title="Verify this payment with Razorpay?"
        message={RECOVERY_CONFIRM_MESSAGE}
        confirmLabel="Verify with Razorpay"
        submitting={isPending}
        submittingLabel="Checking…"
        onCancel={() => setConfirming(false)}
        onConfirm={() => { setConfirming(false); runRecovery(); }}
      />
    </div>
  );
}

function PaymentDrawer({ source, sourceId, onClose }) {
  const { data: payment, isLoading, error, refetch } = useAdminPayment(source, sourceId);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full md:max-w-[min(820px,72vw)] bg-bg-panel border-l border-bg-border flex flex-col animate-slide-in shadow-modal md:self-start md:my-6 md:max-h-[calc(100vh-48px)] overflow-hidden">

        {/* Header — compact */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-bg-border shrink-0">
          <div>
            <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Payment Detail</p>
            <h3 className="font-display font-semibold text-sm text-txt-primary leading-tight mt-0.5">
              {payment?.customer_name ?? payment?.public_reference ?? '…'}
            </h3>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg">
            <X size={15} />
          </button>
        </div>

        {/* Body — max-height + scroll prevents empty panel when content is sparse */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {isLoading && <div className="flex justify-center py-8"><Spinner /></div>}
          {error && (
            <div className="text-xs text-bear py-3">
              {error.message}
              <button onClick={refetch} className="ml-2 underline text-txt-muted">Retry</button>
            </div>
          )}
          {payment && (
            <div className="grid gap-3 lg:grid-cols-2 items-start">
              {/* Left column */}
              <div className="space-y-3">
                <div>
                  <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Payment Summary</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <DetailRow label="ID"             value={payment.id} />
                    <DetailRow label="Public Ref"     value={payment.public_reference} />
                    <DetailRow label="Source"         value={<SourceBadge source={payment.source} />} />
                    <DetailRow label="Source Table"   value={payment.source_table} />
                    <DetailRow label="Source ID"      value={payment.source_id} />
                    <DetailRow label="Status"         value={<StatusBadge status={payment.status} />} />
                    <DetailRow label="Raw Status"     value={payment.raw_status ?? payment.source_status} />
                  </div>
                </div>

                <div>
                  <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Customer</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <DetailRow label="Name"    value={payment.customer_name} />
                    <DetailRow label="Email"   value={payment.customer_email} />
                    <DetailRow label="Phone"   value={payment.customer_phone} />
                    <DetailRow label="User ID" value={payment.user_id != null ? String(payment.user_id) : null} />
                  </div>
                </div>

                <div>
                  <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Product / Plan</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <DetailRow label="Product"      value={payment.product_name} />
                    <DetailRow label="Product Type" value={payment.product_type} />
                    <DetailRow label="Plan ID"      value={payment.plan_id} />
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-3 lg:border-l lg:border-bg-border lg:pl-4">
                <div>
                  <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Amount</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <DetailRow label="Amount"   value={fmtAmount(payment)} />
                    <DetailRow label="Currency" value={payment.currency} />
                  </div>
                </div>

                <div>
                  <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Razorpay</p>
                  <div className="space-y-2">
                    <DetailRow label="Order ID"   value={payment.razorpay_order_id} />
                    <DetailRow label="Payment ID" value={payment.razorpay_payment_id} />
                  </div>
                </div>

                {payment.source === 'BILLING' && (
                  <div>
                    <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Provider / Reservation</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <DetailRow label="Provider State"   value={payment.provider_creation_state} />
                      <DetailRow label="Beneficiary Op."  value={payment.beneficiary_operator_id != null ? String(payment.beneficiary_operator_id) : null} />
                      <DetailRow label="Licenses Held"    value={String(payment.license_reservation_quantity ?? 0)} />
                      <DetailRow label="Reservation"      value={payment.license_reservation_status} />
                    </div>
                  </div>
                )}

                <RecoveryPanel payment={payment} />

                <div>
                  <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Timestamps</p>
                  <div className="space-y-2">
                    <DetailRow label="Created"            value={fmtDateTime(payment.created_at)} />
                    <DetailRow label="Paid"               value={payment.paid_at ? fmtDateTime(payment.paid_at) : null} />
                    <DetailRow label="Verified"           value={payment.verified_at ? fmtDateTime(payment.verified_at) : null} />
                    <DetailRow label="Success Email Sent" value={payment.success_email_sent_at ? fmtDateTime(payment.success_email_sent_at) : null} />
                  </div>
                </div>

                {(payment.user_id != null || payment.website_lead_id != null) && (
                  <div>
                    <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-1">Linked</p>
                    <div className="space-y-2">
                      {payment.user_id != null && (
                        <DetailRow label="User ID" value={String(payment.user_id)} />
                      )}
                      {payment.website_lead_id != null && (
                        <DetailRow label="Lead ID" value={String(payment.website_lead_id)} />
                      )}
                      {payment.user != null && (
                        <>
                          <DetailRow label="User Name"  value={payment.user.name ?? payment.user.full_name} />
                          <DetailRow label="User Email" value={payment.user.email} />
                        </>
                      )}
                      {payment.website_lead != null && (
                        <>
                          <DetailRow label="Lead Name"   value={payment.website_lead.name} />
                          <DetailRow label="Lead Email"  value={payment.website_lead.email} />
                          <DetailRow label="Lead Status" value={payment.website_lead.status} />
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminPayments() {
  const [searchText, setSearchText]         = useState('');
  const [appliedSearch, setAppliedSearch]   = useState('');
  const [sourceFilter, setSourceFilter]     = useState('ALL');
  const [statusFilter, setStatusFilter]     = useState('ALL');
  const [fromDate, setFromDate]             = useState('');
  const [toDate, setToDate]                 = useState('');
  const [page, setPage]                     = useState(0);
  const [selected, setSelected]             = useState(null); // { source, sourceId }

  const params = useMemo(() => ({
    limit:  PAGE_SIZE,
    offset: page * PAGE_SIZE,
    ...(appliedSearch            && { q:          appliedSearch }),
    ...(sourceFilter !== 'ALL'   && { source:     sourceFilter }),
    ...(statusFilter !== 'ALL'   && { status:     statusFilter }),
    ...(fromDate                 && { date_from:  fromDate }),
    ...(toDate                   && { date_to:    toDate }),
  }), [appliedSearch, sourceFilter, statusFilter, fromDate, toDate, page]);

  const { data, isLoading, error, refetch } = useAdminPayments(params);
  const items      = data?.items ?? [];
  const total      = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const applySearch = () => {
    setAppliedSearch(searchText.trim());
    setPage(0);
  };

  const clearAll = () => {
    setSearchText('');
    setAppliedSearch('');
    setSourceFilter('ALL');
    setStatusFilter('ALL');
    setFromDate('');
    setToDate('');
    setPage(0);
  };

  const hasActiveFilters = appliedSearch || sourceFilter !== 'ALL' || statusFilter !== 'ALL' || fromDate || toDate;

  return (
    <div className="max-w-[90rem] space-y-5">
      <SectionHeader
        title="Payments Audit"
        subtitle={total > 0 ? `${total} payments` : undefined}
        actions={
          <button onClick={() => refetch()} className="btn-ghost text-xs flex items-center gap-1.5 py-1.5">
            <RefreshCw size={12} /> Refresh
          </button>
        }
      />

      {/* Filters */}
      <div className="panel p-3 space-y-3">
        {/* Search row */}
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
            placeholder="Search name, email, phone, order ID, payment ID…"
            className="input-base w-80 text-xs"
          />
          <button onClick={applySearch} className="btn-primary text-xs flex items-center gap-1.5 py-1.5">
            <Search size={11} /> Search
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearAll}
              className="text-2xs text-txt-muted hover:text-txt-secondary flex items-center gap-1"
            >
              <X size={11} /> Clear all
            </button>
          )}
        </div>

        {/* Source pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-2xs font-mono text-txt-muted mr-1">Source:</span>
          {['ALL', ...PAYMENT_SOURCES].map((s) => (
            <button
              key={s}
              onClick={() => { setSourceFilter(s); setPage(0); }}
              className={cn(
                'px-2 py-1 rounded text-2xs font-mono transition-colors border',
                sourceFilter === s
                  ? 'bg-amber/10 border-amber/30 text-amber'
                  : 'border-transparent text-txt-muted hover:text-txt-secondary',
              )}
            >
              {s === 'ALL' ? 'ALL' : (SOURCE_LABEL[s] ?? s)}
            </button>
          ))}
        </div>

        {/* Status pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-2xs font-mono text-txt-muted mr-1">Status:</span>
          {['ALL', ...PAYMENT_STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(0); }}
              className={cn(
                'px-2 py-1 rounded text-2xs font-mono transition-colors border',
                statusFilter === s
                  ? 'bg-amber/10 border-amber/30 text-amber'
                  : 'border-transparent text-txt-muted hover:text-txt-secondary',
              )}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Date range */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-2xs font-mono text-txt-muted">From:</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
            className="input-base w-40 text-xs"
          />
          <span className="text-2xs font-mono text-txt-muted">To:</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(0); }}
            className="input-base w-40 text-xs"
          />
          {(fromDate || toDate) && (
            <button
              onClick={() => { setFromDate(''); setToDate(''); setPage(0); }}
              className="text-2xs text-txt-muted hover:text-txt-secondary flex items-center gap-1"
            >
              <X size={11} /> Clear dates
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="panel overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : error ? (
          <div className="flex flex-col items-center py-12 gap-3 text-xs text-bear">
            <p>{error.message}</p>
            <button onClick={() => refetch()} className="btn-ghost text-xs">Retry</button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex justify-center py-12 text-xs text-txt-muted">No payments found.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border">
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted whitespace-nowrap">Created</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Source</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Customer</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Product / Plan</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Amount</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Status</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted whitespace-nowrap">Razorpay Order</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted whitespace-nowrap">Razorpay Payment</th>
                <th className="text-left py-3 px-4 text-2xs font-mono uppercase text-txt-muted">Linked</th>
                <th className="py-3 px-4 text-2xs font-mono uppercase text-txt-muted"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id ?? `${item.source}-${item.source_id}`}
                  onClick={() => setSelected({ source: item.source, sourceId: item.source_id })}
                  className="border-b border-bg-border/50 hover:bg-bg-hover cursor-pointer transition-colors"
                >
                  <td className="py-3 px-4 font-mono text-txt-muted whitespace-nowrap">
                    {fmtDateTime(item.created_at)}
                  </td>
                  <td className="py-3 px-4">
                    <SourceBadge source={item.source} />
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-txt-primary font-medium">{item.customer_name ?? '—'}</div>
                    {item.customer_email && <div className="text-txt-muted">{item.customer_email}</div>}
                    {item.customer_phone && <div className="text-txt-muted">{item.customer_phone}</div>}
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-txt-secondary">{item.product_name ?? '—'}</div>
                    {(item.product_type || item.plan_id) && (
                      <div className="text-txt-muted text-2xs font-mono">
                        {[item.product_type, item.plan_id].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 font-mono text-txt-primary whitespace-nowrap">
                    {fmtAmount(item)}
                  </td>
                  <td className="py-3 px-4">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="py-3 px-4 font-mono text-txt-muted max-w-[160px] truncate">
                    {item.razorpay_order_id ?? '—'}
                  </td>
                  <td className="py-3 px-4 font-mono text-txt-muted max-w-[160px] truncate">
                    {item.razorpay_payment_id ?? '—'}
                  </td>
                  <td className="py-3 px-4 font-mono text-txt-muted text-2xs">
                    {item.user_id != null
                      ? `user #${item.user_id}`
                      : item.website_lead_id != null
                      ? `lead #${item.website_lead_id}`
                      : '—'}
                  </td>
                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setSelected({ source: item.source, sourceId: item.source_id })}
                      className="btn-ghost text-2xs py-1 px-2 border border-bg-border"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-txt-muted">
          <span>
            Page {page + 1} of {totalPages} ({total} total)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-ghost text-xs py-1 px-3 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn-ghost text-xs py-1 px-3 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <PaymentDrawer
          source={selected.source}
          sourceId={selected.sourceId}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
